import { useEffect, useMemo, useState } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { CalendarDays, ChevronLeft, ChevronRight } from 'lucide-react';

export default function HerAttendancePage() {
  const { axiosAuth } = useAuth();
  const api = axiosAuth();
  const [sessions, setSessions] = useState([]);
  const [currentMonth, setCurrentMonth] = useState(new Date());
  const [selectedDate, setSelectedDate] = useState(new Date());

  useEffect(() => {
    api.get('/her/sessions').then(r => setSessions(r.data)).catch(() => {});
  }, []);

  const sessionsByDate = useMemo(() => {
    return sessions.reduce((acc, s) => {
      const key = s.date;
      acc[key] = acc[key] || [];
      acc[key].push(s);
      return acc;
    }, {});
  }, [sessions]);

  const calendarDays = useMemo(() => {
    const year = currentMonth.getFullYear();
    const month = currentMonth.getMonth();
    const start = new Date(year, month, 1);
    const startDay = start.getDay();
    const daysInMonth = new Date(year, month + 1, 0).getDate();
    const totalCells = 42;
    const days = [];
    for (let i = 0; i < totalCells; i += 1) {
      const dayNumber = i - startDay + 1;
      if (dayNumber < 1 || dayNumber > daysInMonth) {
        days.push(null);
      } else {
        days.push(new Date(year, month, dayNumber));
      }
    }
    return days;
  }, [currentMonth]);

  const selectedKey = selectedDate.toISOString().slice(0, 10);
  const selectedSessions = sessionsByDate[selectedKey] || [];

  return (
    <div className="space-y-6" data-testid="her-attendance-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold" style={{ fontFamily: 'Manrope' }}>HER Attendance</h1>
          <p className="text-muted-foreground text-sm">Calendar view of attendance sessions.</p>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" size="icon" onClick={() => setCurrentMonth(new Date(currentMonth.getFullYear(), currentMonth.getMonth() - 1, 1))} data-testid="her-calendar-prev">
            <ChevronLeft size={16} />
          </Button>
          <Button variant="outline" size="icon" onClick={() => setCurrentMonth(new Date(currentMonth.getFullYear(), currentMonth.getMonth() + 1, 1))} data-testid="her-calendar-next">
            <ChevronRight size={16} />
          </Button>
        </div>
      </div>

      <Card className="border-0 shadow-sm rounded-2xl">
        <CardHeader className="pb-2">
          <CardTitle className="text-base font-semibold flex items-center gap-2">
            <CalendarDays className="w-4 h-4 text-rose-600" /> {currentMonth.toLocaleString('default', { month: 'long', year: 'numeric' })}
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-7 gap-2 text-xs text-muted-foreground mb-2">
            {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map(day => (
              <div key={day} className="text-center">{day}</div>
            ))}
          </div>
          <div className="grid grid-cols-7 gap-2">
            {calendarDays.map((date, idx) => {
              const key = date ? date.toISOString().slice(0, 10) : `empty-${idx}`;
              const count = date ? (sessionsByDate[key] || []).length : 0;
              const isSelected = date && key === selectedKey;
              return (
                <button
                  key={key}
                  type="button"
                  onClick={() => date && setSelectedDate(date)}
                  className={`h-16 rounded-xl border text-left px-2 py-1 transition-colors ${date ? 'border-slate-200 hover:border-rose-300' : 'border-transparent'} ${isSelected ? 'bg-rose-50 border-rose-400' : ''}`}
                  data-testid={`her-calendar-cell-${key}`}
                >
                  {date && (
                    <div className="flex flex-col h-full">
                      <span className="text-xs font-semibold text-slate-700">{date.getDate()}</span>
                      {count > 0 && <span className="mt-auto text-[10px] text-rose-700">{count} session{count > 1 ? 's' : ''}</span>}
                    </div>
                  )}
                </button>
              );
            })}
          </div>
        </CardContent>
      </Card>

      <Card className="border-0 shadow-sm rounded-2xl" data-testid="her-attendance-selected">
        <CardHeader className="pb-2">
          <CardTitle className="text-base font-semibold">Sessions on {selectedKey}</CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          {selectedSessions.length ? selectedSessions.map(s => (
            <div key={s.id} className="rounded-xl border border-slate-200 p-3" data-testid={`her-attendance-session-${s.id}`}>
              <p className="text-sm font-semibold">{s.title}</p>
              <p className="text-xs text-muted-foreground">{s.time} • {(s.attendance || []).length} attendees</p>
            </div>
          )) : (
            <p className="text-sm text-muted-foreground">No sessions scheduled.</p>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
